import type { Accent } from '../types';
import { effectiveTts } from './keys';
import { synthesize, ProviderError } from './providers';

// The examiner's voice. Plays a question aloud and resolves when playback ends.
// Prefers the configured cloud voice; on any failure or when no key is set it
// falls back to the browser's OS voice (which gives a genuine en-GB/US/AU voice)
// and reports that it did, so the UI can say so honestly (handoff §10).

interface SpeakResult {
  fallback: boolean; // true when the browser voice was used instead of cloud
  reason?: string;
}

const cache = new Map<string, string>(); // hash → object URL
const MAX_CACHE = 40;

function hash(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (h * 33) ^ s.charCodeAt(i);
  return (h >>> 0).toString(36);
}

class Examiner {
  private audio: HTMLAudioElement | null = null;

  stop(): void {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
      this.audio = null;
    }
    if (typeof speechSynthesis !== 'undefined') speechSynthesis.cancel();
  }

  async speak(text: string, accent: Accent): Promise<SpeakResult> {
    this.stop();
    const eff = effectiveTts();

    if (eff.provider === 'browser') {
      await this.speakBrowser(text, accent);
      return { fallback: eff.usingFallback };
    }

    try {
      const url = await this.getClipURL(text, accent, eff.provider);
      await this.playURL(url);
      return { fallback: false };
    } catch (e) {
      // Honest degrade: keep the session moving on the browser voice.
      const reason = e instanceof ProviderError ? e.message : 'voice unavailable';
      await this.speakBrowser(text, accent);
      return { fallback: true, reason };
    }
  }

  private async getClipURL(text: string, accent: Accent, provider: 'workers' | 'openai' | 'elevenlabs'): Promise<string> {
    const key = `${provider}:${accent}:${hash(text)}`;
    const cached = cache.get(key);
    if (cached) return cached;
    const { blob } = await synthesize(text, accent, provider);
    const url = URL.createObjectURL(blob);
    // Bound the cache — revoke the oldest URL so blobs don't accumulate.
    if (cache.size >= MAX_CACHE) {
      const oldest = cache.keys().next().value as string | undefined;
      if (oldest) {
        URL.revokeObjectURL(cache.get(oldest)!);
        cache.delete(oldest);
      }
    }
    cache.set(key, url);
    return url;
  }

  private playURL(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const a = new Audio(url);
      this.audio = a;
      a.onended = () => resolve();
      a.onerror = () => reject(new Error('audio playback failed'));
      a.play().catch(reject);
    });
  }

  private speakBrowser(text: string, accent: Accent): Promise<void> {
    return new Promise((resolve) => {
      if (typeof speechSynthesis === 'undefined') {
        // No TTS at all — brief silent beat so the loop still advances.
        setTimeout(resolve, 900);
        return;
      }
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(watchdog);
        resolve();
      };
      const u = new SpeechSynthesisUtterance(text);
      u.lang = accent;
      const voice = pickVoice(accent);
      if (voice) u.voice = voice;
      u.rate = 0.98;
      u.onend = finish;
      u.onerror = finish;
      // Watchdog: some browsers never fire onend (or have no voices at all).
      // Estimate ~0.4s per word, floor 2.5s, cap 15s, so ASKING never wedges.
      const est = Math.min(15000, Math.max(2500, text.split(/\s+/).length * 400));
      const watchdog = setTimeout(finish, est);
      speechSynthesis.speak(u);
    });
  }
}

function pickVoice(accent: Accent): SpeechSynthesisVoice | null {
  if (typeof speechSynthesis === 'undefined') return null;
  const voices = speechSynthesis.getVoices();
  if (!voices.length) return null;
  const exact = voices.find((v) => v.lang === accent || v.lang === accent.replace('-', '_'));
  if (exact) return exact;
  const langPrefix = accent.split('-')[0];
  return voices.find((v) => v.lang.startsWith(langPrefix)) || null;
}

// Warm the voice list early (Chrome populates asynchronously).
if (typeof speechSynthesis !== 'undefined') {
  speechSynthesis.getVoices();
  speechSynthesis.onvoiceschanged = () => speechSynthesis.getVoices();
}

export const examiner = new Examiner();
export type { SpeakResult };
